import { useEffect, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import { markPendingNotFound } from '@/lib/analytics/notFoundFlag';

const NOT_FOUND_DESCRIPTION = "The page you're looking for doesn't exist or may have moved.";

export default function NotFound() {
  usePageMeta('Page not found', NOT_FOUND_DESCRIPTION);
  const location = useLocation();

  // Must run before usePostHogPageview's $pageview capture (a passive effect)
  // so that capture can tag this navigation as a 404 instead of real content.
  useLayoutEffect(() => {
    markPendingNotFound();
  }, [location.pathname]);

  // Unmatched routes should never be indexed as if they were real content.
  useEffect(() => {
    let el = document.head.querySelector('meta[name="robots"]');
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', 'robots');
      document.head.appendChild(el);
    }
    el.setAttribute('content', 'noindex, nofollow');
    return () => {
      el?.setAttribute('content', 'index, follow');
    };
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <HomeHeader />
      <div className="max-w-3xl mx-auto px-4 py-24 w-full text-center flex-1">
        <span className="inline-block text-sm font-semibold text-primary uppercase tracking-wider mb-4">
          404
        </span>
        <h1 className="text-3xl md:text-4xl font-bold mb-4">This page doesn't exist.</h1>
        <p className="text-muted-foreground text-lg mb-10">{NOT_FOUND_DESCRIPTION}</p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-white font-semibold hover:bg-primary/90 transition-colors"
          >
            Back to homepage
          </Link>
          <Link
            to="/resources"
            className="inline-flex items-center justify-center rounded-full border border-border px-6 py-3 font-semibold hover:bg-secondary/10 transition-colors"
          >
            Browse resources
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
